import { Component, Inject, Injector, Input, OnDestroy, OnInit } from '@angular/core';
import { APP_BASE_HREF } from '@angular/common';

import { BehaviorSubject, Observable, Subscription, filter } from 'rxjs';

import { ButtonTypesEnum } from '@Common/Forms/enums/button-types.enum';
import { FilterGroupModel } from '@Common/Filter/models/filter-group.model';
import { DistributionService } from '@Common/TargetingShared/services/distribution.service';
import { FilterService } from '@Common/Filter/services/abstract-filter.service';
import { ListPickerService } from '@Common/RhpContexts/services/list-picker.service';
import { messageSeverityEnum } from '@Common/Misc/enums/message-severity.enum';
import { OutletContactTabType, TargetingRhpContextsService } from '@Common/TargetingShared/services/targeting-rhp-contexts.service';
import { SplitHeader } from '@Common/TakeOver/components/TakeOverPanel/take-over-panel.component';

import { AbstractDialogComponent } from '@Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { CoreDialogService } from '@Core/Dialogs/services/dialog.service';
import { FullProfileAnimationModel } from '@Core/Dialogs/models/full-profile-animation.model';
import { OverlayAnimationStateEnum } from '@Core/Dialogs/enums/overlay-animation.enum';
import { FullProfileDialogAnimationsEnum } from '@Core/Dialogs/enums/full-profile-dialog-animations.enum';

import { RecipientType } from '@Discover/Targeting/enums/recipient-type.enum';
import { TargetingSearch } from '@Discover/Targeting/models/targeting-search.model';

import { TargetingResultModel } from '../../../../models/targeting-result.model';
import { TargetingSearchService } from '../../../../services/targeting-search.service';
import { RecipientEditorService } from '../recipient-editor.service';
import { RecommendedRecipientService } from './recommended-recipient-service';
import { QuickEditPanelComponent } from '../QuickEditPanel/quick-edit-panel.component';


@Component({
    selector: 'agpr-recommended-recipient-panel',
    templateUrl: './recommended-recipient-panel.component.html',
    styleUrls: [
        './recommended-recipient-panel.component.scss',
    ],
    providers: [
        {
            provide: 'RecommendedListPickerService',
            useClass: ListPickerService
        },
        {
            provide: ListPickerService,
            useExisting: 'RecommendedListPickerService'
        },
        {
            provide: 'PaginatorServiceInterface',
            useExisting: RecommendedRecipientService
        },
        {
            provide: FilterService,
            useExisting: RecommendedRecipientService
        },
        {
            provide: 'SortOrderServiceInterface',
            useExisting: RecommendedRecipientService
        },
    ],
    standalone: false
})

export class RecommendedRecipientPanelComponent extends AbstractDialogComponent implements OnDestroy, OnInit {

    readonly buttonTypes = ButtonTypesEnum;
    readonly messageSeverityEnum = messageSeverityEnum;

    @Input() overlayContainer: Element;
    @Input() animationTrigger: Observable<FullProfileAnimationModel>;
    @Input() distributionId: number;

    id: string = 'recommended_recipient_panel';

    results: TargetingResultModel[];
    selectedResults: TargetingResultModel[] = [];

    total: number;
    recommendedRecipientListId: number;
    recipientCount: number = 0;

    contactEditorVisible: boolean = false;
    chipsVisible: boolean;
    loading = true;
    initialLoading = true;
    hideSlideOverlay: boolean = false;
    allSelected: boolean = false;
    firstClick: boolean = true;
    selectedContactId: string;

    rhpSelectedTabIndex: number = 0;

    searchModel: TargetingSearch;

    private subscriptions: Subscription[] = [];

    splitHeader: SplitHeader = {
        icon: 'fa-kit-duotone fa-agpr-ai-contacts',
        initialText: '',
        value: '0',
        endText: 'Discover.recommendedRecipient.header.end',
        loading: true
    };

    constructor(
        public recipientEditorService: RecipientEditorService,
        public recommendedRecipientService: RecommendedRecipientService,
        public coreDialogService: CoreDialogService,
        public targetingSearchService: TargetingSearchService,
        protected distributionService: DistributionService,
        public targetingRhpContextsService: TargetingRhpContextsService,
        @Inject('RecommendedListPickerService') public listPickerService: ListPickerService,
        public injector: Injector,
        @Inject(APP_BASE_HREF) public appBaseHref: string,
    ) {
        super(coreDialogService, 'RecommendedRecipientPanel');
    }

    ngOnInit(): void {
        this.recommendedRecipientService.clearFilters();
        this.recommendedRecipientService.clearSearch();
        this.recommendedRecipientService.setCachedResults(this.distributionId);
        this.recommendedRecipientService.getQuickEditQuery(this.distributionId);
        this.subscriptions.push(
            this.recommendedRecipientService.getCachedResults().subscribe((cachedResults) => {
                this.results = cachedResults;
                if (this.results) {
                    this.initialLoading = false;
                }
                if (cachedResults?.length && this.allSelected) {
                    this.selectAllResults();
                }
            }),
            this.recommendedRecipientService.getFilters().subscribe(filters => {
                this.chipsVisible = !!filters.find(filt => {
                    if (filt instanceof FilterGroupModel) {
                        return filt.children.find(x => x.chips && x.chips.length > 0);
                    } else {
                        return filt.chips && filt.chips.length > 0;
                    }
                });
                this.selectNone();
            }),
            this.recommendedRecipientService.getSearchLoading().subscribe(loading => {
                this.loading = loading;
                this.splitHeader.loading = this.loading;
            }),
            this.recommendedRecipientService.getRecipientTotal().subscribe(total => {
                this.total = total;
                this.splitHeader.value = String(total);
            }),
            this.distributionService.getFocusedResource().pipe(filter(x => !!x)).subscribe(distribution => {
                // only get recommended recipients if not loading or already have results
                if (!this.loading && !this.results) {
                    this.recommendedRecipientService.getRecommendedRecipientQuery(distribution.id);
                } else {
                    this.recommendedRecipientService.getFacets();
                }
                if (distribution.recommendedRecipientsListId) {
                    this.recommendedRecipientListId = distribution.recommendedRecipientsListId;
                } else {
                    this.recommendedRecipientListId = null;
                }
            }),
            this.recipientEditorService.getCurrentRecipientInformation().pipe(filter(x => !!x)).subscribe(recipientInfo => {
                this.recipientCount = recipientInfo?.getRecipientGroup(RecipientType.recommended)?.total;
            }),
            this.recommendedRecipientService.getCachedSearch().subscribe((cachedSearch) => {
                this.searchModel = cachedSearch;
            }),
            this.targetingRhpContextsService.getContactId().subscribe(contactId => {
                this.selectedContactId = contactId;
            }),
        );
    }

    cardFocused(entity: TargetingResultModel) {
        this.targetingRhpContextsService.setEntityByResult(entity);
        if (this.firstClick) {
            // need to give time to RHP to enable (aka not be disabled)
            setTimeout(() => {
                this.targetingRhpContextsService.selectOutletContactTab(OutletContactTabType.contactProfile);
            }, 100);
            this.rhpSelectedTabIndex = 1;
            this.firstClick = false;
        }
        this.targetingSearchService.setFocusedResult(entity);
    }

    // The results get their listMembership added on async, this makes the difference show up to angular
    trackResult(index: number, result: TargetingResultModel) {
        return JSON.stringify({
            id: result.id,
            listMemberships: result.listMemberships
        });
    }

    isRecipientPartOfDistribution(recipient: TargetingResultModel): boolean {
        if (this.recommendedRecipientListId && recipient.listMemberships?.length) {
            return recipient.listMemberships.filter(list => list.id === this.recommendedRecipientListId)?.length > 0;
        }
        return false;
    }

    selectAllResults() {
        this.selectedResults = this.results.slice();
        this.listPickerService.setAllResultsSelected(true, this.total);
        this.allSelected = true;
    }

    selectNone(): void {
        this.allSelected = false;
        this.selectedResults = [];
    }

    selectAllInPage(): void {
        this.selectedResults = this.results.slice();
    }

    toggleSelectAll(): void {
        if (this.results.length <= this.selectedResults.length) {
            this.selectNone();
        } else {
            this.selectAllInPage();
        }
    }

    addRecipients() {
        if (this.allSelected) {
            this.recommendedRecipientService.addRecipients(true);
        } else {
            this.recommendedRecipientService.addRecipients(false, this.selectedResults);
        }
    }

    removeRecipients() {
        if (this.allSelected) {
            this.recommendedRecipientService.removeRecipients(true);
        } else {
            this.recommendedRecipientService.removeRecipients(false, this.selectedResults);
        }
    }

    close(): void {
        this.ngOnDestroy();
        this.recipientEditorService.updateRecipients();
        this.hideSlideOverlay = true;
        this.closeDialog();
    }

    selectedTabIndexChange(event) {
        this.rhpSelectedTabIndex = event;
    }

    quickEdit() {
        this.recommendedRecipientService.clearFilters();
        const overlayContainer = document.getElementById('main-content');

        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );
        const dialogIO = {
            inputs: {
                animationTrigger,
                overlayContainer,
                distributionId: this.distributionId,
            },
            outputs: {
                dialogClose: (quickSearchModel?: TargetingSearch) => {
                    if (quickSearchModel) {
                        this.recommendedRecipientService.getSearchFromQuickEdit(quickSearchModel);
                    }
                }
            }
        };
        this.coreDialogService.open(QuickEditPanelComponent, dialogIO, this.injector);
    }

    takeFindings() {
        const filterQuery: any = this.searchModel;
        filterQuery.distributionId = this.distributionId;

        const queryString = TargetingSearch.toFilterQueryString(this.searchModel, this.distributionId);
        window.open(`${this.appBaseHref}Discover/targetingsearches/search?filters=${queryString}`);
    }

    ngOnDestroy() {
        this.subscriptions.forEach(
            subscription => subscription.unsubscribe()
        );
    }
}
